import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import path from 'path';
import EditorLinks from '../editor-links';

export default Vue.component('edit-nav', {
  template: require('./template.html'),
  computed: {
    ...mapGetters(['currentSite', 'viewport']),
    hasCartButton() {
      const { type, addedTypes = [] } = this.currentSite;
      return type === 'products' || addedTypes.includes('products');
    },
  },
  mixins: [guid, modals, uploader],
  components: {
    EditorLinks,
    uploadProgress,
  },
  data() {
    return {
      showConfirm: false,
      showFiles: false,
      loading: false,
      imageFiles: [],
      s3Url: process.env.S3_ASSETS_URL,
      hoverDel: false,
      activeLogo: null,
      activeIndex: null,
      prefix: 'drz_nav_',
      bucketPath: '_droplet-assets/nav/',
      thumbnails: '_droplet-assets/_thumbnails/nav/',
      selectedDirection: this.droplet.slideDirection,
      directions: [
        { direction: 'Left', value: 'left' },
        { direction: 'Center', value: 'center' },
        { direction: 'Right', value: 'right' },
      ],
      selectedScroll: this.droplet.scrollType,
      scrolls: [
        { scroll: 'None', value: 'none' },
        { scroll: 'Fixed', value: 'fixed' },
        { scroll: 'Sticky', value: 'sticky' },
      ],
      selectedAlign: this.droplet.linkAligned,
      aligns: [
        { align: 'Left', value: 'left' },
        { align: 'Center', value: 'center' },
        { align: 'Right', value: 'right' },
      ],
      iconAligns: [
        { align: 'Left', value: 'left' },
        { align: 'Right', value: 'right' },
      ],
      selectedLogoPos: this.droplet.logoAligned,
      logoPositions: [{ position: 'Left', value: 'left' }, { position: 'Right', value: 'right' }],
      siteSearch: false,
      cartButton: false,
    };
  },
  name: 'edit-nav',
  props: ['droplet', 'show-edit-bubble'],
  watch: {
    showEditBubble(open) {
      if (open) {
        this.setInitialValues();
      }
    },
  },
  methods: {
    setInitialValues() {
      this.siteSearch = this.droplet.siteSearch;
      this.cartButton = this.droplet.cartButton;
    },
    setSiteSearch() {
      this.$store.dispatch('setDropletVal', {
        drp: this.droplet,
        key: 'siteSearch',
        val: this.siteSearch,
      });
    },
    toggleBrowse() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchImageFiles();
      }
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    async fetchImageFiles() {
      try {
        this.loading = true;
        const assetsFolder = '/_droplet-assets';
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.thumbnails}`);
        this.imageFiles = files.map(f => `${assetsFolder}${f.split(assetsFolder)[1]}`);
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'fetchImageFiles',
        });
      } finally {
        this.loading = false;
      }
    },
    async setLogoImg(f, def) {
      let file;
      if (def) {
        file = f;
      } else {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      }
      const payload = {};
      payload.drp = this.droplet;
      payload.logo = file;
      return this.$store.dispatch('setNavLogoImg', payload);
    },
    async uploadImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          let newFile = [...e.target.files];
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `${this.prefix}${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');

            try {
              // send off to our s3 upload mixin
              const filePath = `/${this.bucketPath}${fileName}`;
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: newFile,
                thumbnail: {
                  width: 35,
                  srcFile: newFile,
                  name: fileName,
                  bucketPath: `${this._sitePath}/${this.thumbnails}`,
                },
              });
              await this.setLogoImg(filePath);
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadImg - nav',
                });
              }
            } finally {
              if (this.showFiles) {
                setTimeout(() => {
                  this.fetchImageFiles();
                }, 500);
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    toggleConfirm() {
      this.showConfirm = !this.showConfirm;
      const content = this.$refs.confirmModal;
      if (this.showConfirm) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    confirmDelete(file) {
      this.activeLogo = file;
      this.toggleConfirm();
    },
    async deleteLogoImg() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this._getFromThumb(this.activeLogo));
      const thumb = path.basename(this.activeLogo);
      try {
        this.loading = true;
        // delete original from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        });
        // delete thumbnail from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted file: ${file}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteLogoImg',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.loading = false;
        this.fetchImageFiles();
      }
      // if deleting marker that is being used, reset it to default
      if (this.droplet.logoImg === `/${this.bucketPath}${file}`) {
        this.setLogoImg(`${process.env.S3_ASSETS_URL}mock-images/logos/mock-logo.svg`, true);
      }
    },
    matchImg(file) {
      const f = this._getFromThumb(path.basename(file));
      const media = path.basename(this.droplet.logoImg);
      return f === media;
    },
    async setLogo(e) {
      const checked = e.currentTarget.checked;
      const payload = {};
      payload.drp = this.droplet;
      payload.logo = checked;
      await this.$store.dispatch('setNavLogo', payload);
      this.bouncePlugin();
    },
    async setNavScroll(v) {
      const payload = {};
      payload.drp = this.droplet;
      payload.scrolls = this.scrolls;
      payload.scrollType = v;
      this.$store.dispatch('setNavScrollType', payload);
      await this.bouncePlugin();
      this.$emit('observe-position', 'observe');
    },
    setSlideDirection(v) {
      const payload = {};
      payload.drp = this.droplet;
      payload.slideDirection = v;
      this.promise(
        this.$store.dispatch('setNavSlideDirection', payload),
        'setting slide direction',
      );
    },
    setLogoPosition(v) {
      const payload = {};
      payload.drp = this.droplet;
      payload.logoAligned = v;
      payload.logoPositions = this.logoPositions;
      this.promise(this.$store.dispatch('setNavLogoAlign', payload), 'setting logo align');
    },
    setLocals(v) {
      this.$emit('set-display', v);
    },
    setBubbleSize(size) {
      this.$emit('set-bubble-size', size);
    },
    // our global _DropletOptions calls this on cancel if
    // we want any data options in this component reverted
    drzRevertData() {
      this.selectedDirection = this.droplet.slideDirection;
      this.selectedScroll = this.droplet.scrollType;
      this.$emit('css-generator');
      this.setInitialValues();
      this.bouncePlugin();
    },
    drzClickSave() {
      this.bouncePlugin();
    },
    async bouncePlugin() {
      await this.$vnode.context.unBindPlugin();
      await this.$vnode.context.bindPlugin();
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    promise(action, error) {
      const promise = new Promise((resolve, reject) => {
        if (action) {
          resolve();
        } else {
          reject(Error(`Error in Nav: ${error}`));
        }
      });
      promise.then(() => {
        this.bouncePlugin();
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/navigation/components/_edit-options/index.js